import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

export interface PromptOptions {
  title: string;
  body?: ReactNode;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

type PromptFn = (opts: PromptOptions) => Promise<string | null>;

const PromptContext = createContext<PromptFn | null>(null);

export function usePrompt(): PromptFn {
  const fn = useContext(PromptContext);
  return useCallback(
    (opts: PromptOptions) => (fn ? fn(opts) : Promise.resolve(null)),
    [fn],
  );
}

interface PendingPrompt extends PromptOptions {
  resolve: (val: string | null) => void;
}

export function PromptProvider({ children }: { children: ReactNode }): JSX.Element {
  const [pending, setPending] = useState<PendingPrompt | null>(null);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const prompt = useCallback<PromptFn>((opts) => {
    return new Promise<string | null>((resolve) => {
      setValue(opts.defaultValue ?? '');
      setPending({ ...opts, resolve });
    });
  }, []);

  const settle = useCallback(
    (val: string | null) => {
      pending?.resolve(val);
      setPending(null);
    },
    [pending],
  );

  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') settle(null);
    };
    window.addEventListener('keydown', onKey);
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 10);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending, settle]);

  return (
    <PromptContext.Provider value={prompt}>
      {children}
      {pending && (
        <div className="confirm-overlay" onClick={() => settle(null)}>
          <div
            className="confirm-panel"
            role="dialog"
            aria-modal="true"
            aria-label={pending.title}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="confirm-title">{pending.title}</h3>
            {pending.body && <div className="confirm-body">{pending.body}</div>}
            <div className="confirm-body" style={{ marginTop: 12 }}>
              <input
                ref={inputRef}
                className="text-input"
                style={{ width: '100%', boxSizing: 'border-box' }}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') settle(value.trim());
                }}
              />
            </div>
            <div className="confirm-buttons">
              <button type="button" className="btn" onClick={() => settle(null)}>
                {pending.cancelLabel ?? 'Cancel'}
              </button>
              <button
                type="button"
                className="btn btn-start"
                onClick={() => settle(value.trim())}
              >
                {pending.confirmLabel ?? 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PromptContext.Provider>
  );
}
